import { useState, useRef, useEffect, useCallback } from 'react'
import { Upload, CheckCircle, AlertCircle, BookMarked, Library, X, Loader2 } from 'lucide-react'
import api from '../lib/api'
import { bibtexToApa } from '../utils/bibtex'
import { SourceCard } from './ResearchSourcesPanel'

export default function ReferenceUploadPanel({ thesisId, onUploaded }) {
  const [text,    setText]    = useState('')
  const [state,   setState]   = useState('idle')  // idle | loading | done | error
  const [message, setMessage] = useState('')
  const [count,   setCount]   = useState(null)
  const [mode,    setMode]    = useState('append')  // append | replace
  const [picker,  setPicker]  = useState(false)
  const fileRef = useRef(null)
  const [docs, setDocs] = useState([])   // uploaded documents, with what was read from them
  const [list, setList] = useState([])   // the thesis reference list as generation sees it

  const reload = useCallback(async () => {
    try {
      const [d, r] = await Promise.all([api.get(`/thesis/${thesisId}/sources`), api.get(`/thesis/${thesisId}/references`)])
      setDocs(d.data || [])
      setList(r.data || [])
    } catch { /* the panel still works without the lists */ }
  }, [thesisId])
  useEffect(() => { if (thesisId) reload() }, [thesisId, reload])

  const fromFile = new Set(docs.map(d => d.parsed_metadata?.reference_id).filter(Boolean))

  async function submit() {
    if (!text.trim()) return
    if (mode === 'replace' && !window.confirm(
      'Replace the entire reference list for this thesis? The current list will be removed.'
    )) return

    setState('loading')
    setMessage('')
    try {
      const { data } = await api.post(`/thesis/${thesisId}/references`, { text, mode })
      setCount(data.count)
      setMessage(data.message)
      setState('done')
      onUploaded?.()
      reload()
    } catch (err) {
      setMessage(err.response?.data?.error || 'Upload failed')
      setState('error')
    }
  }

  function handleFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => {
      const raw = ev.target.result || ''
      // A .bib file is not APA prose — convert it before the parser sees it.
      setText(/\.bib$/i.test(file.name) ? bibtexToApa(raw) : raw)
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  const isDone  = state === 'done'
  const isError = state === 'error'
  const loading = state === 'loading'

  return (
    <div>
      <textarea
        value={text}
        onChange={e => { setText(e.target.value); setState('idle'); setMessage('') }}
        placeholder={`Paste your APA 7th-edition bibliography here, one reference per paragraph.\n\nExample:\nBandura, A. (1977). Self-efficacy: Toward a unifying theory of behavioral change. Psychological Review, 84(2), 191–215.\n\nBourdieu, P. (1986). The forms of capital. In J. Richardson (Ed.), Handbook of theory and research for the sociology of education (pp. 241–258). Greenwood.`}
        rows={8}
        style={{
          width: '100%', resize: 'vertical', padding: '0.625rem 0.75rem',
          fontSize: '0.75rem', fontFamily: 'monospace', lineHeight: 1.6,
          border: '1px solid #d1d5db', borderRadius: 6, background: '#fff',
          outline: 'none', color: '#374151', boxSizing: 'border-box',
        }}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
        <button
          onClick={submit}
          disabled={loading || !text.trim()}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '0.45rem 0.875rem',
            background: isDone ? '#16a34a' : '#1e3a5f',
            color: isDone ? '#fff' : '#93c5fd',
            border: isDone ? 'none' : '1px solid rgba(147,197,253,0.3)',
            borderRadius: 7, fontSize: '0.78rem', fontWeight: 600,
            cursor: loading || !text.trim() ? 'not-allowed' : 'pointer',
            opacity: loading || !text.trim() ? 0.6 : 1,
          }}
        >
          {isDone ? <CheckCircle size={12} /> : <Upload size={12} />}
          {loading ? 'Saving…' : isDone ? `${count} references saved` : 'Save References'}
        </button>

        <button
          onClick={() => fileRef.current?.click()}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '0.45rem 0.75rem',
            background: 'none', color: '#6b7280',
            border: '1px solid #e5e7eb', borderRadius: 7,
            fontSize: '0.75rem', cursor: 'pointer',
          }}
        >
          <BookMarked size={12} /> Import .txt / .bib
        </button>

        <button
          onClick={() => setPicker(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '0.45rem 0.75rem',
            background: 'none', color: '#6b7280',
            border: '1px solid #e5e7eb', borderRadius: 7,
            fontSize: '0.75rem', cursor: 'pointer',
          }}
        >
          <Library size={12} /> Add from Library
        </button>

        <input ref={fileRef} type="file" accept=".txt,.bib" style={{ display: 'none' }} onChange={handleFile} />
      </div>

      {/* Append vs replace — replacing used to be silent and unconditional */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
        {[['append', 'Add to list'], ['replace', 'Replace whole list']].map(([v, label]) => (
          <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', color: '#6b7280', cursor: 'pointer' }}>
            <input type="radio" name="ref-mode" value={v} checked={mode === v} onChange={() => setMode(v)} />
            {label}
          </label>
        ))}
      </div>

      {message && (
        <p style={{
          display: 'flex', alignItems: 'center', gap: 5,
          marginTop: 8, fontSize: '0.72rem',
          color: isError ? '#dc2626' : '#16a34a',
        }}>
          {isError ? <AlertCircle size={11} /> : <CheckCircle size={11} />}
          {message}
          {isDone && ' — Citation Integrity and Literature Ecology panels will now reflect these references.'}
        </p>
      )}

      <p style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: 8, lineHeight: 1.5 }}>
        Paste your APA bibliography, or import a .txt / .bib file. Separate entries with blank
        lines, or use the Author, A. (Year) start-of-line format. Saved references also appear in
        your Reference Library.
      </p>

      {/* What generation will cite: the list itself, files marked */}
      <div style={{ marginTop: 18 }}>
        <p style={{ fontSize: '0.78rem', fontWeight: 600, color: '#0C1629', marginBottom: 6 }}>
          Reference list used for generation ({list.length})
        </p>
        {list.length === 0 ? (
          <p style={{ fontSize: '0.72rem', color: '#9ca3af', lineHeight: 1.5 }}>
            Empty. Paste a bibliography above, attach from your library, or use an uploaded document below.
          </p>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', maxHeight: 220, overflowY: 'auto' }}>
            {list.map(r => (
              <li key={r.id} style={{ fontSize: '0.72rem', color: '#374151', lineHeight: 1.5, padding: '5px 0', borderBottom: '1px solid #f3f4f6' }}>
                {r.apa_formatted || r.title || r.raw_text}
                {fromFile.has(r.id) && (
                  <span style={{ marginLeft: 6, fontSize: '0.65rem', color: '#0f766e', background: '#0f766e14', padding: '1px 6px', borderRadius: 4 }}>
                    from your file
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Uploaded documents: proof of reading, key points, and "use as reference" */}
      <div style={{ marginTop: 18 }}>
        <p style={{ fontSize: '0.78rem', fontWeight: 600, color: '#0C1629', marginBottom: 6 }}>
          Your uploaded documents ({docs.length})
        </p>
        {docs.length === 0 ? (
          <p style={{ fontSize: '0.72rem', color: '#9ca3af', lineHeight: 1.5 }}>
            None yet. Upload DOCX, DOC or PDF files under Reference Documents below. You will see here what was read from each and can use it as a reference.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {docs.map(d => (
              <SourceCard
                key={d.id} source={d} thesisId={thesisId}
                onChanged={u => { setDocs(prev => prev.map(x => x.id === u.id ? u : x)); reload(); onUploaded?.() }}
              />
            ))}
          </div>
        )}
      </div>

      {picker && (
        <LibraryPicker
          thesisId={thesisId}
          onClose={() => setPicker(false)}
          onAttached={n => {
            setPicker(false)
            setCount(n)
            setMessage(`${n} references attached.`)
            setState('done')
            onUploaded?.()
            reload()
          }}
        />
      )}
    </div>
  )
}

// ─── Attach references that already exist in the user's library ───────────────

function LibraryPicker({ thesisId, onClose, onAttached }) {
  const [refs,     setRefs]     = useState(null)
  const [selected, setSelected] = useState(() => new Set())
  const [query,    setQuery]    = useState('')
  const [busy,     setBusy]     = useState(false)
  const [error,    setError]    = useState('')

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/references')
        setRefs(Array.isArray(data) ? data : [])
      } catch (err) {
        setError(err.response?.data?.error || 'Could not load your library.')
        setRefs([])
      }
    })()
  }, [])

  const q = query.trim().toLowerCase()
  const shown = (refs || []).filter(r => !q ||
    ['authors', 'title', 'journal', 'apa_formatted'].some(k => String(r[k] || '').toLowerCase().includes(q)))

  const toggle = id => setSelected(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  async function attach() {
    if (!selected.size) return
    setBusy(true); setError('')
    try {
      const { data } = await api.post(`/thesis/${thesisId}/references/attach`, {
        referenceIds: [...selected],
      })
      onAttached(data.count)
    } catch (err) {
      setError(err.response?.data?.error || 'Could not attach those references.')
      setBusy(false)
    }
  }

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(12,22,41,0.45)',
               display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 620,
                 maxHeight: '85vh', display: 'flex', flexDirection: 'column', padding: 20 }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <p style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0C1629' }}>Add from Reference Library</p>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8EA4BC' }}>
            <X size={15} />
          </button>
        </div>

        <input
          value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Search your library…"
          style={{ width: '100%', padding: '0.5rem 0.7rem', fontSize: '0.78rem', marginBottom: 10,
                   border: '1px solid #e5e7eb', borderRadius: 7, outline: 'none', boxSizing: 'border-box' }} />

        <div style={{ flex: 1, overflowY: 'auto', minHeight: 120 }}>
          {refs === null && (
            <p style={{ fontSize: '0.75rem', color: '#9ca3af', padding: '1rem 0' }}>Loading…</p>
          )}
          {refs?.length === 0 && (
            <p style={{ fontSize: '0.75rem', color: '#9ca3af', padding: '1rem 0' }}>
              Your library is empty. Add references on the Reference Library page first.
            </p>
          )}
          {shown.map(r => (
            <label key={r.id}
              style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '0.5rem 0.25rem',
                       borderBottom: '1px solid #f3f4f6', cursor: 'pointer', fontSize: '0.75rem' }}>
              <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} style={{ marginTop: 3 }} />
              <span>
                <span style={{ color: '#0C1629', fontWeight: 500 }}>{r.title || '(untitled)'}</span>
                <span style={{ color: '#6b7280' }}> — {r.authors || 'Unknown'} {r.year ? `(${r.year})` : ''}</span>
              </span>
            </label>
          ))}
        </div>

        {error && (
          <p style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 8, fontSize: '0.72rem', color: '#dc2626' }}>
            <AlertCircle size={11} />{error}
          </p>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={onClose}
            style={{ flex: 1, padding: '0.5rem', border: '1px solid #e5e7eb', borderRadius: 7,
                     background: 'none', color: '#6b7280', fontSize: '0.78rem', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={attach} disabled={!selected.size || busy}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                     padding: '0.5rem', border: 'none', borderRadius: 7, background: '#1e3a5f', color: '#fff',
                     fontSize: '0.78rem', fontWeight: 600,
                     cursor: !selected.size || busy ? 'not-allowed' : 'pointer',
                     opacity: !selected.size || busy ? 0.6 : 1 }}>
            {busy && <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />}
            Attach {selected.size || ''}
          </button>
        </div>
      </div>
    </div>
  )
}
